import { useMemo, useState } from 'react';
import T from '../../../../common/components/T';
import { Img, svgSprite, Tooltip } from '../../../../hybrid/wrappers';
import css from './UserPersonalInfo.module.scss';
import classNames from 'classnames';
import Image from 'next/image';
import { copyToClipboard, generateUniqueId } from '../../../../common/utils/utils';
import { useWindowDimensions } from '../../../../common/utils/windowDimensions';
import Branch from '../../../../widgets/grid/cells/Branch/Branch';

export default function UserPersonalInfo(props) {
    const { userData, isSponsor } = props;
    const [contactsModal, showContactsModal] = useState(false);
    const contacts = useMemo(
        () =>
            Object.keys(userData?.contacts || {})
                ?.reduce((accum, key) => {
                    if (userData?.contacts[key]) {
                        accum.push([key, userData?.contacts[key]]);
                    }
                    return accum;
                }, [])
                ?.sort((a) => (a[0] === 'phone' || a[0] === 'email' ? -1 : 1)),
        [userData?.contacts]
    );
    const first2Contacts = contacts?.slice(0, 2)?.reverse();
    const lastContacts = contacts?.slice(2);

    const getIcon = (type) => {
        switch (type) {
            case 'phone':
            case 'email':
                return type;
            case 'whatsapp':
            case 'instagram':
            case 'facebook':
            case 'telegram':
            case 'twitter':
            case 'viber':
            case 'tiktok':
                return `${type}-colored`;
            case 'ok':
                return 'odnoklassniki-colored';
            case 'vk':
                return 'vkontakte-colored';
        }
    };

    const contactHander = (type, value) => {
        showContactsModal(false);
        let href = '';
        switch (type) {
            case 'phone': {
                href = `tel:${value}`;
                break;
            }
            case 'email': {
                href = `mailto:${value}`;
                break;
            }
            case 'whatsapp': {
                href = `https://wa.me/${value}`;
                break;
            }
            case 'instagram': {
                href = `https://instagram.com/${value}`;
                break;
            }
            case 'facebook': {
                href = `https://facebook.com/${value}`;
                break;
            }
            case 'telegram': {
                href = `https://t.me/${value}`;
                break;
            }
            case 'twitter': {
                href = `https://twitter.com/${value}`;
                break;
            }
            case 'viber': {
                href = `viber://pa?chatURI=${value}`;
                break;
            }
            case 'tiktok': {
                href = `www.tiktok.com/${value}`;
                break;
            }
            case 'ok': {
                href = `https://ok.ru/${value}`;
                break;
            }
            case 'vk': {
                href = `https://vk.com/${value}`;
                break;
            }
            default:
                break;
        }

        const link = document.createElement('a');
        link.setAttribute('href', href);
        link.setAttribute('target', '_blank');
        link.click();
        link.remove();
    };

    const isMobile = useWindowDimensions() < 768;

    return (
        <div className={css.wrapper}>
            <div className={css.avatarBlock}>
                <div className={css.mobileUsername}>
                    <p className={classNames(css.label, css.flex)}>
                        {isSponsor ? (
                            <T textName='user-sponsor' defaultStr='Спонсор пользователя' />
                        ) : (
                            <T textName='user' defaultStr='Пользователь' />
                        )}
                    </p>
                    <p className={classNames(css.userFullname, css.flex)}>{userData?.full_name}</p>
                </div>
                <div className={classNames(css.avatarStroke, isSponsor && css.avatarStrokeHidden)}>
                    {svgSprite('user-image-wrap', { className: css.avatarStrokeSvg })}
                </div>
                {userData?.image?.links?.preview ? (
                    <Image
                        src={userData?.image?.links?.preview}
                        className={css.blockHeadAvatarUserImage}
                        width={140}
                        height={140}
                        alt={'Avatar'}
                        quality={100}
                    />
                ) : (
                    <div className={css.blockHeadAvatarUserSvg}>
                        {svgSprite('plug-user-image', { className: css.blockHeadAvatarNoSvg })}
                    </div>
                )}
                <div className={css.avatarMarketingInfo}>
                    {userData?.marketing_profile?.rank ? (
                        <div>
                            <Img
                                className={classNames(css.package, `rank${userData?.marketing_profile?.package?.id || 0}`)}
                                src={`/src/img/ranks/rank${userData?.marketing_profile?.rank?.id}.png`}
                                alt={`Rank ${userData?.marketing_profile?.rank?.id || 0}`}
                            />
                        </div>
                    ) : null}
                    {userData?.marketing_profile?.package ? (
                        <div>
                            <Img
                                className={classNames(css.package, `packageIcon${userData?.marketing_profile?.package?.id || 0}`)}
                                src={`/src/img/packages/package${userData?.marketing_profile?.package?.id || 0}.png`}
                                alt={`Package ${userData?.marketing_profile?.package?.id || 0}`}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            <div className={css.userInfo}>
                <p className={css.label}>
                    {isSponsor ? (
                        <T textName='user-sponsor' defaultStr='Спонсор пользователя' />
                    ) : (
                        <T textName='user' defaultStr='Пользователь' />
                    )}
                </p>
                <p className={css.userFullname}>{userData?.full_name}</p>
                <table className={css.table}>
                    <tbody>
                        <tr>
                            <td>
                                <p>
                                    <T textName='id-of-user' defaultStr='ID' />:
                                </p>
                            </td>
                            <td>
                                <p>{userData?.username}</p>
                            </td>
                            <td align='right'>
                                <Tooltip showModalTooltip={!isMobile} text={<T textName='copy' defaultStr='Копировать' />}>
                                    <div onClick={() => copyToClipboard(userData?.username)}>
                                        {svgSprite('file-copy-gt-system')}
                                    </div>
                                </Tooltip>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    <T textName='package' defaultStr='Пакет' />:
                                </p>
                            </td>
                            <td>
                                <p>{userData?.marketing_profile?.package?.name || '-'}</p>
                            </td>
                            <td align='right'>&nbsp;</td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    <T textName='qualification' defaultStr='Квалификация' />:
                                </p>
                            </td>
                            <td>
                                <p>{userData?.marketing_profile?.rank?.name || '-'}</p>
                            </td>
                            <td align='right'>&nbsp;</td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    <T textName='branch' defaultStr='Ветка' />:
                                </p>
                            </td>
                            <td>
                                <p>
                                    <Branch val={userData?.marketing_profile?.position} />
                                </p>
                            </td>
                            <td align='right'>&nbsp;</td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    <T textName='level' defaultStr='Уровень' />:
                                </p>
                            </td>
                            <td>
                                <p>{+userData?.depth > 0 ? +userData?.depth : '-'}</p>
                            </td>
                            <td align='right'>&nbsp;</td>
                        </tr>
                    </tbody>
                </table>
                <div className={css.contacts}>
                    {first2Contacts?.length
                        ? first2Contacts?.map((contact) => (
                              <Tooltip disableOnMobile={true} text={contact[1]} key={generateUniqueId()}>
                                  <div
                                      onClick={() => contactHander(contact[0], contact[1])}
                                      className={classNames('circleButton', css.contact)}
                                      key={contact[0]}
                                  >
                                      {svgSprite(getIcon(contact[0]), { className: css.contactIcon })}
                                  </div>
                              </Tooltip>
                          ))
                        : null}
                    {lastContacts?.length ? (
                        <div onClick={() => showContactsModal((s) => !s)} className={classNames('circleButton', css.contact)}>
                            {svgSprite('dots-horisontal', { className: css.contactIcon })}
                            {contactsModal ? (
                                <div className={css.contactsModal}>
                                    {lastContacts?.map((contact) => (
                                        <div
                                            onClick={() => contactHander(contact[0], contact[1])}
                                            key={contact[0]}
                                            className={css.contactItem}
                                        >
                                            <div className={css.contactItemIcon}>
                                                {svgSprite(getIcon(contact[0]), { className: css.contactItemIconSvg })}
                                            </div>
                                            <p className={css.contactItemName}>{contact[1]}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
